/**
 * React Motion Scroll Progress Wheel - All Viewports
 * Bottom-right corner position for blog post pages only
 */
import React, { useState, useEffect } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'motion/react';

const ScrollProgressWheel = () => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [readingTime, setReadingTime] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isClientSide, setIsClientSide] = useState(false);

  const prefersReducedMotion = useReducedMotion();
  const circumference = 151; // 2π * 24 (mobile radius)

  useEffect(() => {
    // Initialize client-side state
    setIsClientSide(true);


    // Calculate reading time
    const article = document.querySelector('article') ||
                   document.querySelector('.gh-content') ||
                   document.querySelector('#blog-post-article-content') ||
                   document.querySelector('main');

    if (article) {
      const textContent = article.textContent || article.innerText || '';
      const wordCount = textContent.trim().split(/\s+/).length;
      const estimatedTime = Math.ceil(wordCount / 250); // 250 WPM
      setReadingTime(estimatedTime);
      console.log(`📖 Reading time calculated: ${estimatedTime} minutes (${wordCount} words)`);
    }

    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;

      const scrollProgress = Math.max(0, Math.min(1, scrollTop / (documentHeight - windowHeight)));
      setProgress(scrollProgress);

      // Show when scrolling (after 50px)
      setIsVisible(scrollTop > 50);

      // Calculate remaining reading time
      if (readingTime > 0) {
        const remaining = Math.max(0, readingTime * (1 - scrollProgress));
        setRemainingTime(remaining);
      }
    };

    // Throttled scroll handler
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    // Initial calculation
    updateProgress();

    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [readingTime]);

  const scrollToTop = () => {
    const startY = window.scrollY;
    const startTime = performance.now();
    const duration = Math.min(800, Math.max(300, startY / 3));

    const easeInOutQuart = (t) => {
      return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t;
    };

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const scrollProgress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutQuart(scrollProgress);

      window.scrollTo(0, startY - (startY * easedProgress));

      if (scrollProgress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
    console.log('⬆️ Scrolling to top');

    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  // Only render after client-side initialization
  if (!isClientSide) {
    return null;
  }

  const percentage = Math.round(progress * 100);
  const strokeDashoffset = circumference - (progress * circumference);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
              duration: prefersReducedMotion ? 0 : 0.3,
              ease: "easeOut"
            }
          }}
          exit={{
            opacity: 0,
            scale: 0.8,
            y: 20,
            transition: {
              duration: prefersReducedMotion ? 0 : 0.2
            }
          }}
          className="fixed bottom-20 right-20 z-[9999] cursor-pointer"
          onClick={scrollToTop}
          whileHover={prefersReducedMotion ? {} : { scale: 1.1 }}
          whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          style={{
            filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.3))'
          }}
          aria-label={`Scroll progress ${percentage}% - Click to scroll to top`}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              scrollToTop();
            }
          }}
        >
          {/* Background circle with better light mode visibility */}
          <div className="absolute inset-0 w-16 h-16 rounded-full bg-white/80 dark:bg-black/20 backdrop-blur-sm shadow-lg" />

          {/* Single Progress Ring */}
          <svg
            className="absolute inset-0 -rotate-90 w-16 h-16"
            width="64"
            height="64"
          >
            {/* Background ring */}
            <circle
              cx="32"
              cy="32"
              r="24"
              fill="transparent"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="3"
            />

            {/* Progress ring - single ring only */}
            <motion.circle
              cx="32"
              cy="32"
              r="24"
              fill="transparent"
              stroke="#ff6b35"
              strokeWidth="3"
              strokeLinecap="round"
              style={{
                strokeDasharray: circumference,
              }}
              initial={{ strokeDashoffset: circumference }}
              animate={{
                strokeDashoffset: strokeDashoffset,
                transition: {
                  duration: prefersReducedMotion ? 0 : 0.3,
                  ease: "easeOut"
                }
              }}
            />
          </svg>

          {/* Center Content - positioned to match background circle exactly */}
          <div className="absolute inset-0 w-16 h-16 flex items-center justify-center">
            <motion.div
              className="font-bold text-orange-400 text-xs leading-none"
              key={percentage} // Force re-render for animation
              initial={prefersReducedMotion ? false : { scale: 1.2, opacity: 0.7 }}
              animate={{
                scale: progress > 0.9 ? 1.1 : 1,
                opacity: 1
              }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.2 }}
            >
              {percentage}%
            </motion.div>
          </div>

          {/* Hover glow effect */}
          <motion.div
            className="absolute inset-0 rounded-full opacity-0 w-16 h-16"
            style={{
              background: 'radial-gradient(circle, rgba(255, 107, 53, 0.3) 0%, transparent 70%)'
            }}
            whileHover={prefersReducedMotion ? {} : { opacity: 1 }}
            transition={{ duration: 0.2 }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollProgressWheel;